import {Component, OnInit, ViewEncapsulation} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {AppComponent} from '../app.component';
import {ActivatedRoute} from '@angular/router';
import {CookieHelper} from '../helpers/cookie.helper';
import {ROUTES_PATHS} from "../const/routes";
import {switchMap, map, take, catchError} from "rxjs/operators";
import {getEstimationFromId} from "../helpers/estimation.helper";
import {isSpecialPathForNewRefs} from "../helpers/user-ref.helper";
import {EMPTY, of, throwError} from "rxjs";

@Component({
	selector: 'app-login',
	templateUrl: './login.component.html',
	encapsulation: ViewEncapsulation.None,
	styleUrls: ['./login.component.less']
})
export class LoginComponent implements OnInit {
	textes: any = null;
	modalsTxt: any = null;
	globalTxt: any = null;
	submitted = false;
	byPassWelcome = this.cookieHelper.getCookie(this.cookieHelper.cookieByPassWelcomeName) === 'true';

	loginForm!: FormGroup;

	get f() {
		return this.loginForm.controls;
	}

	constructor(
		public app: AppComponent,
		private formBuilder: FormBuilder,
		private route: ActivatedRoute,
		private cookieHelper: CookieHelper
	) {
	}

	ngOnInit(): void {

		const token = JSON.parse(localStorage.getItem('token')!);

		if (token) {
			localStorage.clear();
		}

		// Init Login form
		this.initForm();

		// Init texts
		this.initTexts();

		// Handle query params
		this.handleQueryParams();
	}

	//============================================================= Connexion
	onSubmit() {
		this.submitted = true;

		if (!this.loginForm.value.Login || !this.loginForm.value.Password) {
			this.openModalError(this.globalTxt.formFields.errorFormInvalid);
			return;
		}

		this.login(this.loginForm.value.Login, this.loginForm.value.Password);
	}

	login(_login: string, _password: string) {

		this.app.apiToolsService.loginUsr(_login, _password)
			.pipe(
				switchMap(loginResp => {
					// clear the localStorage
					localStorage.clear();

					// stocker token
					localStorage.setItem('token', JSON.stringify(loginResp));

					// récupérer les refs juste après le login et propager loginResp
					return this.app.apiToolsService.getAllDataRef('REF')
						.pipe(map((refs: any) => ({loginResp, refs})));
				})
			).subscribe({
			next: ({loginResp, refs}: { loginResp: any; refs: any }) => {
				// navigation / suite du flow de login selon outOfSimu
				let outOfSimu: boolean = loginResp?.outOfSimu;
				if (outOfSimu) this.app.router.navigate([ROUTES_PATHS.MON_COMPTE]);
				else {
					if (this.byPassWelcome == true) {
						this.app.router.navigate([ROUTES_PATHS.CHOIX_RENOV])
					} else if (this.byPassWelcome == false || this.byPassWelcome == null) {
						this.app.router.navigate([ROUTES_PATHS.WELCOME])
					}
				}
			},
			error: err => {
				console.error('Login or refs fetch failed', err);
				console.log('ERR loginUsr ==> ', err.status)
				if (err.status === 404) {
					this.openModalError(this.modalsTxt.modalLoginError);
				} else if (err.status === 401) {
					this.openModalError(this.modalsTxt.modalUnauthorized);
				} else if (err.status === 403) {
					this.openModalError(this.modalsTxt.modalZeroEstimation);
				} else {
					this.openModalError(this.modalsTxt.modalErrorApi);
				}
			}
		});
	}

	//============================================================= Private functions
	private handleQueryParams(): void {
		this.route.queryParamMap.pipe(
			take(1),

			switchMap((params) => {

				const uuid = params.get('uuid') || '';
				const guid = params.get('guid') || '';
				const simUuid = params.get('simUuid') || '';
				const checkSimulationRemains = params.has('checkSimulationRemains')
					? params.get('checkSimulationRemains') === 'true'
					: true;

				// UUID flow (non bloquant)
				if (uuid) {
					this.handleUserUuid(uuid);
				}

				// No guid => stop
				if (!guid) {
					return EMPTY;
				}

				// Login
				return this.app.apiToolsService.loginUsrByGuid(guid).pipe(
					switchMap((user: any) => {
						console.log('res loginUsrByGuid ==> ', user);

						if (!user) {
							return EMPTY;
						}

						// Save token
						localStorage.setItem('token', JSON.stringify(user));
						localStorage.setItem('guid',JSON.stringify(user.guid));
						if (simUuid) localStorage.setItem('simUuid', JSON.stringify(simUuid));

						const outOfSimu = user?.outOfSimu && checkSimulationRemains;

						// Get backUrl
						return this.app.sharedToolsService
							.getParamSettings('back_url')
							.pipe(
								switchMap((backUrl: string) => {
									const isSpecialPath = isSpecialPathForNewRefs(this.app.sharedToolsService);

									// OUT OF SIMU
									if (outOfSimu) {
										if (isSpecialPath) {
											this.app.confirmDialogService.confirmThis(
												"",
												this.modalsTxt.modalZeroEstimation,
												"alert",
												"",
												() => {
													this.app.router.navigate([ROUTES_PATHS.PRODUITS]);
												},
												() => {}
											);
										} else {
											location.href = backUrl;
										}
										return EMPTY;
									}

									// NORMAL FLOW
									if (!isSpecialPath) {
										if (this.byPassWelcome === true) {
											this.app.router.navigate([ROUTES_PATHS.CHOIX_RENOV]);
										} else {
											this.app.router.navigate([ROUTES_PATHS.WELCOME]);
										}
										return EMPTY;
									}

									// SPECIAL PATH => simUuid required
									if (!simUuid) {
										return of({type: 'MISSING_SIMUUID'});
									}

									// simUuid => simId
									return this.app.apiToolsService
										.getSimIdBySimUuid(simUuid)
										.pipe(
											// 404 here means the simulation does not exist (not a login error)
											catchError((err: any) => err?.status === 404
												? of({type: 'SIMULATION_NOT_FOUND'})
												: throwError(err)),
											switchMap((result: any) => {
												if (result?.type === 'SIMULATION_NOT_FOUND') {
													return of(result);
												}
												console.log('result ==> ', result);
												return getEstimationFromId(
													result?.id,
													this.app.apiToolsService
												);
											})
										);
								})
							);
					})
				);
			})
		).subscribe({
			next: (result: any) => {
				if (result?.type === 'MISSING_SIMUUID') {
					this.openModalError(this.modalsTxt.modalSimUuidError);
					return;
				}
				if (result?.type === 'SIMULATION_NOT_FOUND') {
					this.openModalError(this.modalsTxt.modalSimulationNotFound);
					return;
				}
				// simUuid + redirectTo=rooms goes to the rooms page, otherwise to the quick estimation
				const params = this.route.snapshot.queryParamMap;
				const goToRooms = !!params.get('simUuid') && params.get('redirectTo') === 'rooms';
				this.app.router.navigate([goToRooms ? ROUTES_PATHS.PIECES : ROUTES_PATHS.ESTIMATION_RAPIDE]);
			},

			error: (err) => {
				console.error(err);
				if (err.status === 404) {
					this.openModalError(this.modalsTxt.modalLoginError);
				} else {
					this.openModalError(this.modalsTxt.modalErrorApi);
				}
			}
		});
	}

	private handleUserUuid(uuid: string): void {
		console.log('uuid ==> ', uuid);
		this.app.apiToolsService.validUsrByUuid(uuid)
			.pipe(take(1))
			.subscribe({
				next: (res: any) => {
					console.log('res validUsrByUuid ==> ', res);
					if (res?.refId) {
						localStorage.setItem('refId', JSON.stringify(res.refId));
					}
				},
				error: (err) => {
					console.log('[ERR] validUsrByUuid ==> ',err.status);
					if (err.status === 404) {
						this.openModalError(this.modalsTxt.modalUuidError);
						console.log('[404] uuid not found !');
						return;
					}
					this.openModalError(this.modalsTxt.modalErrorApi);
				}
			});
	}

	private initForm(): void {
		this.loginForm = this.formBuilder.group({
			Login: ['', [Validators.required, Validators.email]],
			Password: ['', Validators.required]
		});
	}

	private initTexts(): void {

		// Modal texts
		this.app.dataInitService.getModalTextsOnce(
			(modalsTxt) => {

				this.modalsTxt = modalsTxt;

				console.log(
					'this.modalsTxt ==> ',
					this.modalsTxt
				);
			}
		);

		// Global texts
		this.app.dataInitService.getGlobalTextsOnce(
			(globalTxt) => {

				this.globalTxt = globalTxt;

				this.app.sharedToolsService
					.loadTexts('txtLoginMdpOublie')
					.subscribe((textes) => {

						this.textes = textes;

						console.log(
							'this.textes ==> ',
							this.textes
						);
					});
			}
		);
	}

	private openModalError(message:string): void {
		this.app.confirmDialogService.confirmThis(
			"",
			message,
			"alert",
			"",
			() => {},
			() => {}
		);
	}

	// ngOnInit(): void {
	// 	let token = JSON.parse(localStorage.getItem("token")!);
	// 	if (token) localStorage.clear();
	//
	// 	// Get the modal texts
	// 	this.app.dataInitService.getModalTextsOnce(modalsTxt => {
	// 		this.modalsTxt = modalsTxt;
	// 		console.log("this.modalsTxt ==> ", this.modalsTxt);
	// 	});
	//
	// 	this.route.queryParamMap.subscribe(params => {
	// 		const uuid: string = params.get('uuid') || '';
	//
	// 		if (uuid != '') {
	// 			console.log("uuid ==> ", uuid);
	//
	// 			this.app.apiToolsService.validUsrByUuid(uuid).subscribe((res: any) => {
	// 				console.log("res validUsrByUuid ==> ", res);
	// 				if (res?.refId) {
	// 					console.log("res refId ==> ", res.refId);
	// 					localStorage.setItem('refId', JSON.stringify(res.refId));
	// 				}
	// 			}, err => {
	// 				console.log('[ERR] validUsrByUuid ==> ', err.status);
	//
	// 				if (err.status === 404) {
	// 					console.log("[404] uuid not found !");
	// 				} else {
	// 					this.app.confirmDialogService.confirmThis("", this.modalsTxt.modalErrorApi, "alert", "",
	// 						() => {
	// 						},
	// 						() => {
	// 						});
	// 				}
	// 			});
	// 		}
	//
	// 		const guid: string = params.get('guid') || '';
	// 		const simUuid: string = params.get('simUuid') || '';
	//
	// 		if (guid != '') {
	// 			let backUrl = '';
	//
	// 			this.app.apiToolsService.loginUsrByGuid(guid).subscribe(
	// 				(res: any) => {
	// 					console.log("res loginUsrByGuid ==> ", res);
	//
	// 					// TODO: DELETE TEST => Adding a SimId in token
	// 					const HARD_CODED_SIM_ID: number = 85794;
	//
	// 					if (res) {
	// 						localStorage.setItem("token", JSON.stringify(res));
	//
	// 						// Save the guid in localStorage for redirection if the paiement is KO
	// 						localStorage.setItem('guid', JSON.stringify(res.guid));
	//
	// 						// Get backUrl value
	// 						this.app.sharedToolsService.getParamSettings('back_url').subscribe((url: string) => {
	// 							console.log("url ==> ", url);
	// 							backUrl = url;
	// 						});
	//
	// 						// Check if refId in special path
	// 						const _isSpecialPathForNewRefs: boolean = isSpecialPathForNewRefs(this.app.sharedToolsService);
	//
	// 						// Check if outOfSimu is true or false
	// 						const outOfSimu: boolean = res?.outOfSimu;
	//
	// 						// TODO: ********************* Test : /?guid=9a23f62b-0896-4817-8b36-8a63b8012c64?simUuid=02788917-632b-4cde-9124-c452e7028d00
	// 						/*
	// 						Testing
	// 						USR_NUMERO_EXTERNE = 9a23f62b-0896-4817-8b36-8a63b8012c64
	// 						SIN_ID = 85801
	// 						SIN_UUID = 02788917-632b-4cde-9124-c452e7028d00
	// 						 */
	// 						if (_isSpecialPathForNewRefs) {
	// 							if (outOfSimu) {
	// 								this.app.confirmDialogService.confirmThis("", this.modalsTxt.modalZeroEstimation, "alert", "",
	// 									() => {
	// 										this.app.router.navigate([ROUTES_PATHS.PRODUITS])
	// 									}, () => {
	// 									});
	// 							} else {
	// 								// TODO: DELETE TEST => Adding a SimId in token
	// 								const tokenWithSim = {...res, simulationId: HARD_CODED_SIM_ID};
	// 								localStorage.setItem("token", JSON.stringify(tokenWithSim));
	//
	// 								const simId: number = tokenWithSim.simulationId;
	//
	// 								// Create in localStorage BATIMENT / RENOVATION / arrPieces
	// 								getEstimationFromId(simId, this.app.apiToolsService).pipe(take(1)).subscribe({
	// 									next: () => {
	// 										// Redirection
	// 										this.app.router.navigate([ROUTES_PATHS.ESTIMATION_RAPIDE]);
	// 									},
	// 									error: (err) => {
	// 										console.error('Erreur getEstimationFromId depuis Login', err);
	// 									}
	// 								});
	// 							}
	// 						} else {
	// 							if (outOfSimu) location.href = backUrl;
	// 							else {
	// 								if (this.byPassWelcome == true) {
	// 									this.app.router.navigate([ROUTES_PATHS.CHOIX_RENOV])
	// 								} else if (this.byPassWelcome == false || this.byPassWelcome == null) {
	// 									this.app.router.navigate([ROUTES_PATHS.WELCOME])
	// 								}
	// 							}
	// 						}
	// 					}
	// 				}, err => {
	// 					console.log('ERR loginUsr ==> ', err.status)
	// 					if (err.status === 404) {
	// 						this.app.confirmDialogService.confirmThis("", this.modalsTxt.modalLoginError, "alert", "",
	// 							() => {
	// 								location.href = backUrl;
	// 							}, () => {
	// 							});
	// 					} else {
	// 						this.app.confirmDialogService.confirmThis("", this.modalsTxt.modalErrorApi, "alert", "",
	// 							() => {
	// 								location.href = backUrl;
	// 							}, () => {
	// 							});
	// 					}
	// 				})
	// 		}
	// 	});
	//
	// 	this.loginForm = this.formBuilder.group(
	// 		{
	// 			Login: ['', [Validators.required, Validators.email]],
	// 			Password: ['', Validators.required]
	// 		}
	// 	)
	//
	// 	// Get the global texts
	// 	this.app.dataInitService.getGlobalTextsOnce(globalTxt => {
	// 		this.globalTxt = globalTxt;
	//
	// 		// // Get the modal texts
	// 		// this.app.dataInitService.getModalTextsOnce(modalsTxt => {
	// 		// 	this.modalsTxt = modalsTxt;
	// 		// });
	//
	// 		// Load the texts for the login page
	// 		this.app.sharedToolsService.loadTexts('txtLoginMdpOublie')
	// 			.subscribe(textes => {
	// 				this.textes = textes;
	// 				console.log("this.textes ==> ", this.textes);
	// 			});
	// 	})
	// }

	public readonly ROUTES_PATHS = ROUTES_PATHS;
}
